import { ReactNode } from 'react';
import { cn } from '@/lib/utils.ts';
import { CHART_PALETTE } from '@/components/ui/chartPalette.ts';

export interface BarListRow {
  id: string;
  label: ReactNode;
  /** Kontekst obok etykiety — widoczny tylko w układzie `stacked`. */
  note?: ReactNode;
  value: ReactNode;
  /** Wypełnienie paska, 0–100. */
  pct: number;
  /** Barwa paska; bez niej wszystkie idą barwą wspólną dla listy. */
  color?: string;
}

interface BarListProps extends React.HTMLAttributes<HTMLDivElement> {
  rows: BarListRow[];
  /**
   * `stacked` — etykieta i wartość nad paskiem na całą szerokość; czyta się
   * jak ranking. `inline` — etykieta, pasek i wartość w jednej linii; mieści
   * dwa zestawienia obok siebie w jednej sekcji.
   */
  layout?: 'stacked' | 'inline';
  /** Wspólna barwa pasków. Wiersz może ją nadpisać własnym `color`. */
  color?: string;
}

/**
 * Lista z paskiem procentowym — udziały źródeł rezerwacji, przedziały wiekowe,
 * koszty po kategoriach. Procent podaje wywołanie: komponent nie zna całości,
 * z której liczyłby udział.
 */
const BarList = ({
  rows,
  layout = 'stacked',
  color = CHART_PALETTE.accent,
  className,
  ...props
}: BarListProps) => {
  const inline = layout === 'inline';

  return (
    <div
      className={cn('flex flex-col', inline ? 'gap-2.5' : 'gap-3', className)}
      {...props}
    >
      {rows.map((row) => (
        <div
          key={row.id}
          className={inline ? 'flex items-center gap-3' : undefined}
        >
          {inline ? (
            <span className="w-[104px] shrink-0 text-sm text-neutral-700">
              {row.label}
            </span>
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-neutral-700">{row.label}</span>
              {row.note && (
                <span className="text-[13px] text-neutral-400">{row.note}</span>
              )}
              <span className="ml-auto text-[15px] font-bold tabular-nums text-ink">
                {row.value}
              </span>
            </div>
          )}

          <div
            className={cn(
              'h-2 overflow-hidden rounded-full bg-neutral-100',
              inline ? 'flex-1' : 'mt-1.5',
            )}
          >
            <span
              className="block h-full"
              style={{
                width: `${row.pct}%`,
                backgroundColor: row.color ?? color,
              }}
            />
          </div>

          {inline && (
            <span className="w-9 shrink-0 text-right text-sm font-bold tabular-nums text-ink">
              {row.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

export default BarList;
